import { IAttachment, ILogicForValue, IQuestion, ISection, ITableColumn, ITableRow } from './interface';

/** Visits every question (parents first, then children) in display order. */
export function forEachQuestion(sections: ISection[], callback: (question: IQuestion) => void) {
    const walk = (question: IQuestion) => {
        callback(question);
        (question.childQuestion || []).forEach(walk);
    };
    sections.forEach(section => section.fields.forEach(walk));
}

/** A child question is shown only when its parent's current answer matches `matchingValue`. */
export function isChildVisible(child: IQuestion, parentAnswer: string) {
    if (!child.matchingValue) {
        return true;
    }
    return (parentAnswer || '').trim() === child.matchingValue.trim();
}

/** Returns the questions of a section that are currently visible, based on the given answers. */
export function getVisibleQuestions(section: ISection, answers: { [questionId: string]: string }) {
    const visible: IQuestion[] = [];
    const walk = (question: IQuestion) => {
        visible.push(question);
        (question.childQuestion || [])
            .filter(child => isChildVisible(child, answers[question.questionId]))
            .forEach(walk);
    };
    section.fields.forEach(walk);
    return visible;
}

export function getColumnLabel(column: ITableColumn) {
    return column.fieldName || column.columnName;
}

export function childColumnKey(columnId: string, childIndex: number) {
    return `${columnId}|${childIndex}`;
}

export function clientValueKey(columnId: string) {
    return `__client|${columnId}`;
}

export function systemValueKey(columnId: string) {
    return `__system|${columnId}`;
}

export function evidenceKey(columnId: string) {
    return `__evidence|${columnId}`;
}

export function filesKey(columnId: string) {
    return `__files|${columnId}`;
}

/** Proofs attached by the admin that are not saved yet. */
export function newFilesKey(columnId: string) {
    return `__newFiles|${columnId}`;
}

export const ROW_NOT_DELETABLE_KEY = '__rowNotDeletable';

function isFileColumn(column: ITableColumn) {
    return column.questionType === 'File' || column.inputType === 'file';
}

/** The API repeats the same child column; keeps the first of each columnId with its original index. */
function uniqueChildColumns(column: ITableColumn, isFile: boolean) {
    const seen = new Set<string>();
    return (column.childColumns || [])
        .map((child, index) => ({ child, index }))
        .filter(({ child }) => {
            if (isFileColumn(child) !== isFile || seen.has(child.columnId)) {
                return false;
            }
            seen.add(child.columnId);
            return true;
        });
}

/** Editable child columns shown under a table cell (e.g. "As of date", "Lender"). */
export function getEditableChildColumns(column: ITableColumn) {
    return uniqueChildColumns(column, false);
}

/** File upload child columns (e.g. "Attach Proof"). */
export function getFileChildColumns(column: ITableColumn) {
    return uniqueChildColumns(column, true).map(x => x.child);
}

export function parseNumber(value: string | undefined | null) {
    const amount = Number(String(value || '').replace(/[$,\s]/g, ''));
    return isNaN(amount) ? 0 : amount;
}

/** True when a child column applies for the parent value, e.g. matchingCondition '>' and matchingValue '0'. */
export function isConditionMet(parentValue: string, matchingValue?: string | null, matchingCondition?: string | null) {
    if (matchingValue === undefined || matchingValue === null || matchingValue === '') {
        return true;
    }
    const value = (parentValue || '').trim();
    const condition = (matchingCondition || '=').trim();
    if (['>', '>=', '<', '<='].includes(condition)) {
        if (!value) {
            return false;
        }
        const left = parseNumber(value), right = parseNumber(matchingValue);
        switch (condition) {
            case '>': return left > right;
            case '>=': return left >= right;
            case '<': return left < right;
            default: return left <= right;
        }
    }
    const isEqual = value.toLowerCase() === matchingValue.trim().toLowerCase();
    return condition === '!=' || condition === '<>' ? !isEqual : isEqual;
}

/**
 * Evaluates a calculated value from its logicsForValue steps.
 * Each step's value comes from `getStepValue`; the operator of a step joins it with the next step.
 */
export function calculateLogics(logics: ILogicForValue[], getStepValue: (logic: ILogicForValue) => number) {
    const steps = [...logics].sort((a, b) => (a.orderOfExecution || 0) - (b.orderOfExecution || 0));
    let result = 0;
    let operator = '+';
    steps.forEach(step => {
        const value = getStepValue(step);
        switch (operator) {
            case '-': result -= value; break;
            case '*': result *= value; break;
            case '/': result = value ? result / value : 0; break;
            default: result += value;
        }
        operator = step.nextColumnExecutionOperator || '+';
    });
    return Math.round(result * 100) / 100;
}

/** Formats a calculated number; amounts keep 2 decimals. */
export function formatCalculatedValue(value: number, inputFormat: string | null) {
    return inputFormat === 'amount' ? value.toFixed(2) : String(value);
}

/** True when the value already holds this number (empty counts as 0), so the backend formatting is kept. */
export function isSameNumber(value: string | undefined, calculated: number) {
    return Math.abs(parseNumber(value) - calculated) < 0.005;
}

function isMetaKey(key: string) {
    return key.startsWith('__');
}

/** Converts the API table rows (`updatedColumn`) into flat rows keyed by columnId. */
export function toTableRows(question: IQuestion): ITableRow[] {
    return (question.updatedColumn || []).map(cells => {
        const row: ITableRow = {};
        cells.forEach(cell => {
            row[cell.columnId] = cell.response || '';
            if (cell.clientValue !== undefined && cell.clientValue !== null) {
                row[clientValueKey(cell.columnId)] = String(cell.clientValue);
            }
            if (cell.systemValue !== undefined && cell.systemValue !== null) {
                row[systemValueKey(cell.columnId)] = String(cell.systemValue);
            }
            if (cell.evidence !== undefined && cell.evidence !== null) {
                row[evidenceKey(cell.columnId)] = String(cell.evidence);
            }
            if (cell.rowNotDeletable) {
                row[ROW_NOT_DELETABLE_KEY] = 'true';
            }
            const files: IAttachment[] = [...(cell.files || [])];
            (cell.childColumns || []).forEach((child, index) => {
                row[childColumnKey(cell.columnId, index)] = child.response || '';
                (child.files || []).forEach(file => {
                    if (!files.some(x => x.fileId === file.fileId)) {
                        files.push(file);
                    }
                });
            });
            if (files.length > 0) {
                row[filesKey(cell.columnId)] = JSON.stringify(files);
            }
        });
        return row;
    });
}

/** Builds an empty row for the given table columns. */
export function emptyTableRow(columns: ITableColumn[]): ITableRow {
    const row: ITableRow = {};
    columns.forEach(column => {
        row[column.columnId] = '';
        (column.childColumns || []).forEach((_child, index) => {
            row[childColumnKey(column.columnId, index)] = '';
        });
    });
    return row;
}

/**
 * Converts flat rows back into the API `updatedColumn` structure for saving.
 * File upload child columns are left out so existing attachments are not overwritten.
 */
export function toApiTableRows(columns: ITableColumn[], rows: ITableRow[]) {
    return rows.map(row => columns.map(column => {
        const cell: { [key: string]: any } = {
            columnId: column.columnId,
            response: (row[column.columnId] || '').trim()
        };
        if (row[ROW_NOT_DELETABLE_KEY]) {
            cell.rowNotDeletable = true;
        }
        const childColumns = getEditableChildColumns(column)
            .map(({ child, index }) => ({
                columnId: child.columnId,
                response: (row[childColumnKey(column.columnId, index)] || '').trim()
            } as { [key: string]: any }));
        // Only new proofs are sent; saved files are kept by the API (upsert by fileId).
        const newFiles = getRowFiles(row, column.columnId, true);
        const proofColumn = getFileChildColumns(column)[0];
        if (proofColumn && newFiles.length > 0) {
            childColumns.push({
                columnId: proofColumn.columnId,
                questionType: 'File',
                files: newFiles.map(file => ({ fileName: file.fileName, size: file.size, fileBase64String: file.fileBase64String }))
            });
        }
        if (childColumns.length > 0) {
            cell.childColumns = childColumns;
        }
        return cell;
    }));
}

export interface IValidationRule {
    isMandatory: boolean;
    minLength: number | null;
    maxLength: number | null;
    regexValidation: string | null;
    errorMessage: string;
    inputType: string | null;
    inputFormat: string | null;
}

/** Fields of a question or table column that decide what can be typed. */
export type IInputRule = Pick<IValidationRule, 'inputType' | 'inputFormat' | 'regexValidation' | 'maxLength'>;

/** Masked values returned by the API (e.g. XXXXX1234) cannot be validated against the field format. */
export function isMaskedValue(value: string) {
    return /^[X*]+\d*$/i.test((value || '').trim());
}

/** Keeps only digits and inserts the separators of a pattern like 'XXX-XXX-XXXX' or 'XX/XX/XXXX'. */
function applyDigitPattern(value: string, pattern: string) {
    const digits = value.replace(/\D/g, '');
    let result = '';
    let digitIndex = 0;
    for (let i = 0; i < pattern.length && digitIndex < digits.length; i++) {
        if (pattern[i] === 'X') {
            result += digits[digitIndex++];
        }
        else {
            result += pattern[i];
        }
    }
    return result;
}

/** Filters characters not allowed by a simple `^[...]+$` regex from the API (e.g. letters only for names). */
function filterByCharacterClass(value: string, regexValidation: string | null) {
    // Only a single character class, e.g. ^[A-Za-z\s.]+$ — not patterns like the email regex ^[^\s@]+@[^\s@]+\.[^\s@]+$.
    const match = (regexValidation || '').match(/^\^\[([^\]]+)\][+*]\$$/);
    if (!match) {
        return value;
    }
    try {
        const allowed = new RegExp(`^[${match[1]}]$`);
        return value.split('').filter(ch => allowed.test(ch)).join('');
    }
    catch {
        return value;
    }
}

/** Restricts what can be typed in a field, based on its input format / type from the API. */
export function sanitizeInput(value: string, rule: IInputRule) {
    switch (rule.inputFormat) {
        case 'date': return applyDigitPattern(value, 'XX/XX/XXXX');
        case 'phone': return applyDigitPattern(value, 'XXX-XXX-XXXX');
        case 'ein': return applyDigitPattern(value, 'XX-XXXXXXX');
        case 'ssn': return applyDigitPattern(value, 'XXX-XX-XXXX');
        case 'zip': {
            const digits = value.replace(/\D/g, '');
            return digits.length > 5 ? applyDigitPattern(digits, 'XXXXX-XXXX') : digits;
        }
        case 'amount': {
            // Digits with a single decimal point and at most 2 decimals.
            const cleaned = value.replace(/[^\d.]/g, '');
            const [whole, ...rest] = cleaned.split('.');
            return rest.length > 0 ? `${whole}.${rest.join('').slice(0, 2)}` : whole;
        }
    }
    if (rule.inputType === 'numeric') {
        return value.replace(/\D/g, '');
    }
    return filterByCharacterClass(value, rule.regexValidation);
}

/** Max input length for formatted fields; otherwise the API maxLength. */
export function getInputMaxLength(rule: IInputRule) {
    switch (rule.inputFormat) {
        case 'date': return 10;
        case 'phone': return 12;
        case 'zip': return 10;
        case 'ein': return 10;
        case 'ssn': return 11;
        default: return rule.maxLength || undefined;
    }
}

function isValidDate(value: string) {
    const match = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (!match) {
        return false;
    }
    const month = Number(match[1]), day = Number(match[2]), year = Number(match[3]);
    const date = new Date(year, month - 1, day);
    return year >= 1900 && year <= 2100 && date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
}

/** Validates one value against the rules from the API. Returns the error message, or '' when valid. */
export function validateValue(value: string, rule: IValidationRule, label: string) {
    const trimmed = (value || '').trim();
    if (!trimmed) {
        return rule.isMandatory ? `${label} is required.` : '';
    }
    if (isMaskedValue(trimmed)) {
        return '';
    }
    if (rule.inputFormat === 'date' && !isValidDate(trimmed)) {
        return `${label} must be a valid date (MM/DD/YYYY).`;
    }
    if (rule.inputFormat === 'amount' && isNaN(Number(trimmed.replace(/,/g, '')))) {
        return `${label} must be a valid amount.`;
    }
    if (rule.minLength && trimmed.length < rule.minLength) {
        return rule.errorMessage || `${label} must be at least ${rule.minLength} characters.`;
    }
    if (rule.maxLength && trimmed.length > rule.maxLength) {
        return rule.errorMessage || `${label} must be at most ${rule.maxLength} characters.`;
    }
    if (rule.regexValidation) {
        try {
            if (!new RegExp(rule.regexValidation).test(trimmed)) {
                return rule.errorMessage || `${label} is not in a valid format.`;
            }
        }
        catch {
            // Ignore invalid regex configuration from the API.
        }
    }
    return '';
}

/** Bytes from a size label like "0.03 MB", "13 KB" or "512 B"; 0 when it cannot be read. */
export function parseFileSize(size: string | null | undefined) {
    const match = (size || '').trim().match(/^([\d.]+)\s*(B|KB|MB|GB)?$/i);
    if (!match) {
        return 0;
    }
    const units: { [unit: string]: number } = { B: 1, KB: 1024, MB: 1024 * 1024, GB: 1024 * 1024 * 1024 };
    return Number(match[1]) * units[(match[2] || 'B').toUpperCase()];
}

/** Size label in the API format: MB with 2 decimals, KB for small files. */
export function formatFileSize(bytes: number) {
    return bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(2)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

/** Files attached to a table cell (stored on the row as JSON). */
export function getRowFiles(row: ITableRow, columnId: string, isNew: boolean = false): IAttachment[] {
    try {
        return JSON.parse(row[isNew ? newFilesKey(columnId) : filesKey(columnId)] || '[]');
    }
    catch {
        return [];
    }
}

export function isEmptyTable(rows: ITableRow[] | undefined) {
    return !rows || rows.length === 0 ||
        rows.every(row => Object.keys(row).filter(key => !isMetaKey(key)).every(key => !(row[key] || '').trim()));
}

export function formatPlaceholder(inputFormat: string | null, placeholder: string | null) {
    switch (inputFormat) {
        case 'date': return 'MM/DD/YYYY';
        case 'phone': return 'XXX-XXX-XXXX';
        case 'zip': return 'XXXXX';
        case 'ein': return 'XX-XXXXXXX';
        case 'ssn': return 'XXX-XX-XXXX';
        case 'amount': return '0.00';
        default: return placeholder || '';
    }
}

/** Formats a value for read-only display; amounts get a $ prefix and thousands separators. */
export function formatDisplayValue(value: string, inputFormat: string | null) {
    if (!value) {
        return '';
    }
    if (inputFormat === 'amount') {
        const amount = Number(value.replace(/,/g, ''));
        if (!isNaN(amount)) {
            return '$' + amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        }
    }
    return value;
}
